import React, { useEffect, useMemo, useCallback, useRef, useState, memo } from 'react';
import { countQuestions } from '../lib/questionCount';
import type { ContentItem, ContentConfig, ContentResult, BehaviorConfig } from '../schema';
import { applyEmbedLanguageToConfig } from '../schema/i18n';
import { applyHideCorrectAnswersToConfig, redactCorrectAnswersFromResult } from '../schema/config';
import type { GradingResult, ImperativeSubmit, ImperativeSubmitOptions } from '../schema/grading';
import { isGradingUnavailableError } from '../schema/grading';
import { ContentRegistry } from '../renderer/registry';
import { ContentWrapper } from '../components/ContentWrapper';
import { FeedbackDisplay, getFeedbackType } from '../components/FeedbackDisplay';
import { ScoreSummary } from '../components/ScoreDisplay';
import { NavigationButtons } from '../components/NavigationButtons';
import { TimerDisplay } from '../components/TimerDisplay';
import { useContentState } from '../hooks/useContentState';
import { useAttempt } from '../hooks/useAttempt';
import { useTimer } from '../hooks/useTimer';
import { useLocale } from '../hooks/useLocale';
import { usePeriodicAnswerFlush } from '../hooks/usePeriodicAnswerFlush';
import { cn } from '../lib/utils';
import { ContentThemeProvider, type ContentThemeSkin } from '../theme';
import { ContentPlayerActionsContext } from './ContentPlayerActionsContext';
import { ContentPlayerNavLeadingContext } from './ContentPlayerNavLeadingContext';
import { clearEmbedPreviewSession } from '@ai-content-hub/content-session';

/**
 * Merge element data with behavior config overrides
 * Behavior config takes precedence over element data settings
 */
function mergeDataWithBehavior<T>(data: T, behavior?: BehaviorConfig): T {
    if (!behavior || typeof data !== 'object' || data === null) {
        return data;
    }

    return {
        ...data,
        ...(behavior.shuffleChoices !== undefined && { shuffleChoices: behavior.shuffleChoices }),
    };
}

function defaultAnswerIsSubmittable(answer: unknown): boolean {
    if (answer === null || answer === undefined) return false;
    if (typeof answer === 'string') return answer.trim() !== '';
    if (Array.isArray(answer)) return answer.length > 0;
    if (typeof answer === 'object')
        return Object.keys(answer as Record<string, unknown>).length > 0;
    return true;
}

function shouldHideContentPlayerNavigationForMcqTabs(
    contentType: string,
    layout: unknown
): boolean {
    if (!layout || typeof layout !== 'object') return false;
    const displayMode = (layout as { displayMode?: string }).displayMode;
    const isMultiPage = displayMode === 'tabs' || displayMode === 'step';
    if (!isMultiPage) return false;
    return contentType === 'lesson-mcq' || contentType === 'mcq-set';
}

function stableUnknownFingerprint(value: unknown): string {
    if (value === undefined || value === null) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return JSON.stringify(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function buildContentResult(
    content: ContentItem,
    answer: unknown,
    grading: GradingResult,
    attemptsCount: number,
    timeSpentMs: number
): ContentResult {
    return {
        contentId: content.id,
        contentType: content.type,
        isCorrect: grading.isCorrect,
        score: grading.score,
        min: 0,
        max: grading.max,
        percentage: grading.max > 0 ? (grading.score / grading.max) * 100 : 0,
        answer,
        correctAnswer: grading.correctAnswer,
        attempts: attemptsCount,
        timeSpent: timeSpentMs,
        timestamp: Date.now(),
        details: grading.details,
    };
}

export interface ContentPlayerProps {
    content: ContentItem;
    config?: ContentConfig;
    onAnswer?: (answer: unknown) => void;
    /** May return a Promise (e.g. persist + ack); `handleSubmit` resolves only after it settles. */
    onSubmit?: (result: ContentResult) => void | Promise<unknown>;
    onComplete?: (result: ContentResult) => void;
    onRetry?: () => void;
    onNext?: () => void;
    onPrevious?: () => void;
    initialAnswer?: unknown;
    /** When reviewing a stored session, hydrate the result panel without re-calling async graders. */
    initialResult?: ContentResult;
    showFeedback?: boolean;
    isReview?: boolean;
    disabled?: boolean;
    showNavigation?: boolean;
    showTimer?: boolean;
    className?: string;
    /**
     * When true, do not apply `config.theme` on the wrapper so Tailwind tokens
     * (`bg-primary`, etc.) match the host app — use beside ContentEditor preview.
     */
    useHostTheme?: boolean;
    /**
     * EAA-1677: resolved tenant skin, sourced by the caller (host app reads its
     * own theme state; an embed page reads its own bootstrap/theme fetch) and
     * stamped as `data-skin` on the player root so embeds without `:root`
     * access still render playful-skin CSS. Omit to inherit an ancestor's
     * `data-skin` (e.g. the app's own `<html>`) via the CSS cascade instead.
     * Ignored when `useHostTheme` is true — those embeds must only inherit.
     */
    skin?: ContentThemeSkin;
    /**
     * When > 0, periodically invoke `onAutoPersistAnswer` with the current answer
     * if it changed since the last flush (seconds, clamped 3–600). Embed URLs: `freqSec=`.
     */
    autoPersistAnswerIntervalSec?: number;
    /** See `autoPersistAnswerIntervalSec`. */
    onAutoPersistAnswer?: (answer: unknown) => void;
    /**
     * Optional mutable ref that, when provided, is populated with the internal
     * submit function.  The parent can call `ref.current?.()` to trigger the
     * full grade → score → onSubmit flow programmatically (e.g. FORCE_SUBMIT
     * from a parent iframe).
     *
     * Returns `true` when grading actually starts, `false` when the submit was
     * skipped (no answer, disabled, already submitting, etc.).  The caller can
     * use the return value to send an immediate `PLAYER_SUBMIT({ skipped })` so
     * the LMS parent does not wait for a timeout.
     */
    imperativeSubmitRef?: React.MutableRefObject<ImperativeSubmit | undefined>;
    /**
     * Optional learner id for async AI graders (e.g. speaking multipart `user_id`).
     * Forwarded as `userId` on {@link GradingContext}. Embed URLs: `userId=`.
     */
    userId?: string;
    /**
     * Session JWT for write-auth AI graders (e.g. essay, speaking in multi-player mode).
     * Forwarded as `sessionToken` on {@link GradingContext} so graders can call
     * `SessionTokenAuthentication` endpoints without overwriting the global view-token.
     */
    sessionToken?: string;
    /**
     * Host tenant id, forwarded to player components that call authoring-time AI
     * endpoints when the JWT carries no tenant (staff/superadmin) — e.g.
     * interactive-reading pronunciation scoring. Embed URLs: `tenant_id=`.
     */
    tenantId?: string;
    /** Host UI locale — overrides config.i18n.defaultLanguage */
    language?: string;
    /**
     * ELMS-2370: LMS "hide answer" assignments (embed `hideAnswer=true`). Forces
     * `config.behavior.showCorrectAnswer=false` AFTER merging content.config, so the
     * content's own config cannot re-enable it.
     */
    hideCorrectAnswers?: boolean;
}

type ContentPlayerSlotProps = {
    PlayerComponent: React.ElementType;
    playerData: unknown;
    learnerAnswer: unknown;
    onAnswer: (newAnswer: unknown) => void;
    disabled: boolean;
    showFeedback: boolean;
    result: unknown;
    isReview: boolean;
    config: ContentConfig;
    tenantId?: string;
    contentId?: string;
    sessionToken?: string;
    language?: string;
};

const ContentPlayerSlot = memo(function ContentPlayerSlot({
    PlayerComponent,
    playerData,
    learnerAnswer,
    onAnswer,
    disabled,
    showFeedback,
    result,
    isReview,
    config,
    tenantId,
    contentId,
    sessionToken,
    language,
}: ContentPlayerSlotProps) {
    return (
        <div className="content-player-body pb-4">
            <PlayerComponent
                data={playerData}
                answer={learnerAnswer}
                onAnswer={onAnswer}
                disabled={disabled}
                showFeedback={showFeedback}
                result={result}
                isReview={isReview}
                config={config}
                tenantId={tenantId}
                contentId={contentId}
                sessionToken={sessionToken}
                language={language}
            />
        </div>
    );
});

/**
 * ContentPlayer - Renders and manages a content item for learner interaction
 */
export function ContentPlayer({
    content,
    config: configOverride,
    onAnswer,
    onSubmit,
    onComplete,
    onRetry,
    onNext,
    onPrevious,
    initialAnswer,
    initialResult,
    showFeedback: showFeedbackProp,
    isReview = false,
    disabled = false,
    showNavigation = true,
    showTimer: showTimerProp,
    className,
    useHostTheme = false,
    skin,
    autoPersistAnswerIntervalSec,
    onAutoPersistAnswer,
    imperativeSubmitRef,
    userId,
    sessionToken,
    tenantId,
    language,
    hideCorrectAnswers = false,
}: ContentPlayerProps): React.ReactElement {
    const config = useMemo(
        () => {
            const merged = {
                ...configOverride,
                ...content.config,
            };
            // Apply the host `language` prop as the highest-priority override
            // for config.i18n.defaultLanguage. This ensures graders (essay,
            // speaking) receive the correct language for AI APIs, regardless
            // of what the content author set or the API returned.
            const withLanguage = language
                ? (applyEmbedLanguageToConfig(merged, language) ?? merged)
                : merged;
            return applyHideCorrectAnswersToConfig(withLanguage, hideCorrectAnswers) as typeof merged;
        },
        [content.config, configOverride, language, hideCorrectAnswers]
    );

    const scoringCfg = config.scoring;
    const scoringFingerprint = useMemo(
        () =>
            [
                scoringCfg?.max,
                scoringCfg?.partialCreditEnabled,
                scoringCfg?.penaltyPerWrongAnswer,
                scoringCfg?.passingScore,
                (scoringCfg as { aiGradingEnabled?: boolean } | undefined)?.aiGradingEnabled,
                (scoringCfg as { gradingQuestionType?: string } | undefined)?.gradingQuestionType,
                (scoringCfg as { userId?: string } | undefined)?.userId,
            ].join('\0'),
        [
            scoringCfg?.max,
            scoringCfg?.partialCreditEnabled,
            scoringCfg?.penaltyPerWrongAnswer,
            scoringCfg?.passingScore,
            (scoringCfg as { aiGradingEnabled?: boolean } | undefined)?.aiGradingEnabled,
            (scoringCfg as { gradingQuestionType?: string } | undefined)?.gradingQuestionType,
            (scoringCfg as { userId?: string } | undefined)?.userId,
        ]
    );

    const initialResultFingerprint = useMemo(() => {
        if (!initialResult) {
            return '';
        }
        return [
            initialResult.contentId,
            initialResult.isCorrect,
            initialResult.score,
            initialResult.max,
            initialResult.timestamp,
            initialResult.attempts,
        ].join('\0');
        // Avoid depending on `initialResult` identity — parent may pass a new object each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialResult?.contentId,
        initialResult?.isCorrect,
        initialResult?.score,
        initialResult?.max,
        initialResult?.timestamp,
        initialResult?.attempts,
    ]);

    const initialAnswerFingerprint = useMemo(
        () => stableUnknownFingerprint(initialAnswer),
        [initialAnswer]
    );

    const hideNavigationForMcqTabsMode = useMemo(
        () => shouldHideContentPlayerNavigationForMcqTabs(content.type, config.layout),
        [content.type, config.layout]
    );

    const effectiveShowNavigation = showNavigation && !hideNavigationForMcqTabsMode;

    const registration = useMemo(() => {
        return ContentRegistry.getInstance().get(content.type);
    }, [content.type]);

    const inlinePlayerSubmit = registration?.definition.inlinePlayerSubmit ?? false;

    const { t } = useLocale({ config: config.i18n, initialLanguage: language });

    const {
        state: contentState,
        answer,
        setAnswer,
        showFeedback,
        setShowFeedback,
        isCorrect,
        setResult,
        submit,
        resetForRetry,
        review,
        isInteractive,
    } = useContentState({
        contentId: content.id,
        initialState: initialAnswer ? { selectedAnswer: initialAnswer } : undefined,
    });

    const {
        attempts,
        canAttempt,
        canRetry,
        submit: submitAttempt,
        retry: retryAttempt,
        timeSpent,
    } = useAttempt({
        contentId: content.id,
        maxAttempts: config.behavior?.maxAttempts,
        allowRetry: config.behavior?.allowRetry,
    });

    const submitRef = useRef<() => void>();
    const lastGradeResultRef = useRef<GradingResult | undefined>(undefined);
    const reviewHydratedRef = useRef(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [retryEpoch, setRetryEpoch] = useState(0);
    const [navLeading, setNavLeading] = useState<React.ReactNode>(null);
    const [navLeadingKey, setNavLeadingKey] = useState('');
    const prevNavLeadingContentIdRef = useRef<string | null>(null);
    /** Keeps per-item grading details in state so `gradingResultForPlayer` memo updates when details exist. */
    const [playerGradeDetails, setPlayerGradeDetails] = useState<
        Record<string, unknown> | undefined
    >(undefined);

    const registerNavLeading = useCallback((node: React.ReactNode | null, revision?: string) => {
        setNavLeading(node);
        setNavLeadingKey(revision ?? '');
    }, []);

    useEffect(() => {
        setPlayerGradeDetails(undefined);
        const prevId = prevNavLeadingContentIdRef.current;
        if (prevId !== null && prevId !== content.id) {
            setNavLeading(null);
            setNavLeadingKey('');
        }
        prevNavLeadingContentIdRef.current = content.id;
    }, [content.id]);

    useEffect(() => {
        setRetryEpoch(0);
    }, [content.id]);

    const learnerAnswer = useMemo(
        () => answer ?? (retryEpoch > 0 ? undefined : initialAnswer),
        [answer, retryEpoch, initialAnswer]
    );

    const behavior = config.behavior;
    const behaviorApplied = behavior != null;
    const behaviorShuffleChoices = behavior?.shuffleChoices;
    const playerData = useMemo(
        () => mergeDataWithBehavior(content.data, behavior),
        // behavior is often a new object each parent render; merge only uses shuffleChoices
        // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize via primitives above
        [content.data, behaviorApplied, behaviorShuffleChoices]
    );

    const timerEnabled = config.timer?.enabled && config.timer?.duration;
    const {
        remaining,
        isRunning,
        isWarning,
        isExpired,
        start: startTimer,
        pause: pauseTimer,
    } = useTimer({
        contentId: content.id,
        duration: config.timer?.duration ?? 0,
        warningThreshold: config.timer?.warningThreshold,
        autoSubmitOnExpire: config.timer?.autoSubmitOnExpire,
        onExpire: () => {
            if (config.timer?.autoSubmitOnExpire && submitRef.current) {
                submitRef.current();
            }
        },
    });

    useEffect(() => {
        if (timerEnabled && !isReview) {
            startTimer();
        }
    }, [timerEnabled, isReview, startTimer]);

    useEffect(() => {
        if (isReview) {
            review();
        }
    }, [isReview, review]);

    useEffect(() => {
        reviewHydratedRef.current = false;
    }, [content.id, isReview, initialResultFingerprint, initialAnswerFingerprint]);

    useEffect(() => {
        if (reviewHydratedRef.current) {
            return;
        }
        if (initialResult) {
            lastGradeResultRef.current = {
                isCorrect: initialResult.isCorrect,
                score: initialResult.score,
                max: initialResult.max,
                feedback: undefined,
                correctAnswer: initialResult.correctAnswer,
                details: initialResult.details,
            };
            setPlayerGradeDetails(initialResult.details as Record<string, unknown> | undefined);
            setResult(initialResult.isCorrect, initialResult.score, initialResult.max, undefined);
            setShowFeedback(true);
            if (!isReview) {
                submit();
            }
            reviewHydratedRef.current = true;
            return;
        }

        if (!isReview) {
            return;
        }

        const grader = registration?.definition.grader;
        const effectiveAnswer = learnerAnswer;
        if (!grader || effectiveAnswer == null) {
            return;
        }

        // Review never starts network/AI grading (it would 403 for viewer tokens and bill AI).
        if (grader.requiresNetwork?.(content.data, config.scoring)) {
            return;
        }

        const raw = grader.grade(content.data, effectiveAnswer as never, config.scoring, {
            contentId: content.id,
            config,
            ...(userId ? { userId } : {}),
            ...(sessionToken ? { sessionToken } : {}),
            // EAA-2117: graders send the tenant on the unsaved-draft grade path.
            ...(tenantId ? { tenantId } : {}),
        });
        if (raw instanceof Promise) {
            return;
        }

        lastGradeResultRef.current = raw;
        setPlayerGradeDetails(raw.details as Record<string, unknown> | undefined);
        setResult(raw.isCorrect, raw.score, raw.max, raw.feedback);
        setShowFeedback(true);
        reviewHydratedRef.current = true;
        // `initialResult` / full `config` omitted: use fingerprints so referentially-unstable props do not re-hydrate in a loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isReview,
        initialResultFingerprint,
        registration,
        answer,
        learnerAnswer,
        initialAnswerFingerprint,
        content.data,
        content.id,
        scoringFingerprint,
        userId,
        tenantId,
        setResult,
        setShowFeedback,
        submit,
    ]);

    const handleAnswerChange = useCallback(
        (newAnswer: unknown) => {
            if (!isInteractive || disabled) return;
            console.log('[GRADE-FLOW] 1. handleAnswerChange — học sinh thay đổi đáp án', {
                contentId: content.id,
                contentType: content.type,
                answer: newAnswer,
            });
            setAnswer(newAnswer as null);
            onAnswer?.(newAnswer);
        },
        [disabled, isInteractive, onAnswer, setAnswer]
    );

    const applyGradedResult = useCallback(
        (gradingResult: GradingResult, answerForResult: unknown) => {
            console.log('[GRADE-FLOW] 3. applyGradedResult — kết quả chấm điểm nhận được', {
                contentId: content.id,
                contentType: content.type,
                isCorrect: gradingResult.isCorrect,
                score: gradingResult.score,
                max: gradingResult.max,
            });
            lastGradeResultRef.current = gradingResult;
            setPlayerGradeDetails(gradingResult.details as Record<string, unknown> | undefined);
            setResult(
                gradingResult.isCorrect,
                gradingResult.score,
                gradingResult.max,
                gradingResult.feedback
            );
            submit();
            setShowFeedback(true);

            submitAttempt(
                answerForResult,
                gradingResult.isCorrect,
                gradingResult.score,
                gradingResult.max,
                gradingResult.feedback
            );

            if (timerEnabled) {
                pauseTimer();
            }

            const builtResult = buildContentResult(
                content,
                answerForResult,
                gradingResult,
                attempts,
                timeSpent
            );
            // ELMS-2370: LMS "hide answer" — never post correctAnswer / answer-bearing details out.
            const result = hideCorrectAnswers
                ? redactCorrectAnswersFromResult(builtResult)
                : builtResult;

            console.log('[GRADE-FLOW] 4. buildContentResult — ContentResult đã xây dựng', {
                contentId: result.contentId,
                score: result.score,
                max: result.max,
            });
            console.log('[GRADE-FLOW] 5. gọi onSubmit(result) → EmbedPlayerPage.handleSubmit');
            // ELMS-2365: keep the (possibly async) onSubmit promise so handleSubmit — and the
            // FORCE_SUBMIT flight awaiting it — only finishes after persist + ack.
            const submitDone = Promise.resolve(onSubmit?.(result)).catch(() => undefined);

            if (gradingResult.isCorrect || !canRetry) {
                console.log('[GRADE-FLOW] 6. gọi onComplete(result) → EmbedPlayerPage.handleComplete', {
                    reason: gradingResult.isCorrect ? 'isCorrect' : 'no more retries',
                });
                onComplete?.(result);
            }
            return submitDone;
        },
        [
            attempts,
            canRetry,
            content,
            hideCorrectAnswers,
            onComplete,
            onSubmit,
            pauseTimer,
            setResult,
            setShowFeedback,
            submit,
            submitAttempt,
            timeSpent,
            timerEnabled,
        ]
    );

    const runSubmit = useCallback(async (opts?: ImperativeSubmitOptions): Promise<boolean | 'deferred'> => {
        if (!canAttempt || disabled || isSubmitting) {
            console.log('[GRADE-FLOW] 2a. handleSubmit bị block', { canAttempt, disabled, isSubmitting });
            return false;
        }

        const effectiveAnswer = learnerAnswer;
        const grader = registration?.definition.grader;

        if (grader?.validateAnswer) {
            if (!grader.validateAnswer(effectiveAnswer as never, content.data as never, {
                allowPartial: opts?.deferAiGrading === true,
            })) {
                console.log('[GRADE-FLOW] 2b. handleSubmit — validateAnswer FAIL, bỏ qua');
                return false;
            }
        } else if (!defaultAnswerIsSubmittable(effectiveAnswer)) {
            console.log('[GRADE-FLOW] 2b. handleSubmit — không có đáp án hợp lệ, bỏ qua');
            return false;
        }

        console.log('[GRADE-FLOW] 2. handleSubmit bắt đầu chấm điểm', {
            contentId: content.id,
            contentType: content.type,
            grader: grader ? grader.constructor?.name || 'defined' : 'none',
            answer: effectiveAnswer,
        });

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            let gradingResult: GradingResult;
            if (grader) {
                const raw = grader.grade(content.data, effectiveAnswer as never, config.scoring, {
                    contentId: content.id,
                    config,
                    ...(userId ? { userId } : {}),
                    ...(sessionToken ? { sessionToken } : {}),
                    // EAA-2117: the draft grade-job (unsaved preview) and SpeechAce need the
                    // tenant when the author's JWT carries none (staff/superadmin).
                    ...(tenantId ? { tenantId } : {}),
                    // ELMS-2377: host FORCE_SUBMIT only (see GradingContext.deferAiResult).
                    ...(opts?.deferAiGrading ? { deferAiResult: true } : {}),
                });
                const isAsync = raw instanceof Promise;
                console.log('[GRADE-FLOW] 2c. grader.grade() gọi xong', { isAsync });
                gradingResult = await Promise.resolve(raw);
                if (isAsync) {
                    console.log('[GRADE-FLOW] 2d. Promise resolve — kết quả AI grading nhận được', {
                        score: gradingResult.score,
                        max: gradingResult.max,
                    });
                }
            } else {
                gradingResult = {
                    isCorrect: false,
                    score: 0,
                    max: config.scoring?.max ?? 1,
                };
                console.log('[GRADE-FLOW] 2c. không có grader → kết quả mặc định 0 điểm');
            }

            if (gradingResult.pending) {
                // ELMS-2377: AI grade-job(s) exist server-side, the backend stamps the score on
                // the session when they finish. Persist/show nothing now (no fake score) and let
                // the host ack immediately.
                console.log('[GRADE-FLOW] 2f. AI grade-job đã tạo, không chờ kết quả (deferred)');
                return 'deferred';
            }
            await applyGradedResult(gradingResult, effectiveAnswer);
            return true;
        } catch (err) {
            // EAA-2117: sharedApiClient rejects with a plain `ApiError` object
            // (`{ message, status }`), not an Error, so a BE refusal of a grade-job used to
            // fall through to the generic "Submit failed" and hide the BE's own message.
            const apiMessage =
                err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
                    ? (err as { message: string }).message
                    : '';
            // ELMS-2369: a failed AI grade never shows server/technical text to the learner.
            const message = isGradingUnavailableError(err)
                ? t('gradingUnavailable', 'We couldn\'t grade your answer yet. Please try again later.')
                : err instanceof Error
                    ? err.message
                    : typeof err === 'string'
                      ? err
                      : apiMessage || t('submitFailed', 'Submit failed');
            console.error('[GRADE-FLOW] 2e. grader.grade() ném lỗi', err);
            setSubmitError(message);
            return false;
        } finally {
            setIsSubmitting(false);
        }
    }, [
        learnerAnswer,
        applyGradedResult,
        canAttempt,
        config.scoring,
        content.data,
        content.id,
        disabled,
        config,
        isSubmitting,
        registration,
        userId,
        tenantId,
    ]);

    /** Learner-facing submit (button, timer auto-submit): always waits for the real result. */
    const handleSubmit = useCallback(async (): Promise<boolean> => (await runSubmit()) === true, [runSubmit]);

    useEffect(() => {
        submitRef.current = handleSubmit;
        if (imperativeSubmitRef) {
            imperativeSubmitRef.current = runSubmit;
        }
    }, [handleSubmit, runSubmit, imperativeSubmitRef]);

    usePeriodicAnswerFlush({
        answer: learnerAnswer,
        intervalSec: autoPersistAnswerIntervalSec ?? 0,
        enabled: Boolean(
            onAutoPersistAnswer && !isReview && !disabled && (autoPersistAnswerIntervalSec ?? 0) > 0
        ),
        contentId: content.id,
        onFlush: (a) => {
            onAutoPersistAnswer?.(a);
        },
    });

    const handleRetry = () => {
        if (!canRetry) return;
        if (content.id) {
            clearEmbedPreviewSession(String(content.id));
        }
        lastGradeResultRef.current = undefined;
        setPlayerGradeDetails(undefined);
        retryAttempt();
        resetForRetry();
        setShowFeedback(false);
        setSubmitError(null);
        setRetryEpoch((n) => n + 1);
        onRetry?.();
    };

    const PlayerComponent = registration?.definition.PlayerComponent;

    const showTimerDisplay = showTimerProp ?? timerEnabled;
    const parentAllowsFeedback = showFeedbackProp !== false;
    const shouldShowFeedback = parentAllowsFeedback && showFeedback;

    const answerValidForSubmit = useMemo(() => {
        const validateAnswer = registration?.definition.grader?.validateAnswer;
        if (typeof validateAnswer === 'function') {
            return validateAnswer(learnerAnswer as never, content.data as never);
        }
        return defaultAnswerIsSubmittable(learnerAnswer);
    }, [registration, learnerAnswer, content.data]);

    const baseSubmitNavEnabled = canAttempt && !isReview && !shouldShowFeedback && !disabled;

    const submitDisabledHintNav =
        baseSubmitNavEnabled && !answerValidForSubmit && !isSubmitting
            ? content.type === 'question-set' || content.type === 'qt-dropdown'
                ? t('answerAtLeastOneQuestion', 'Answer at least one question to submit')
                : t('answerAllQuestions', 'Answer all questions to submit')
            : undefined;

    const gradingResultForPlayer = useMemo(() => {
        if (!shouldShowFeedback || isCorrect === null) return undefined;
        const details =
            playerGradeDetails ??
            (lastGradeResultRef.current?.details as Record<string, unknown> | undefined);
        return {
            isCorrect,
            score: contentState.score ?? 0,
            max: contentState.max ?? 1,
            feedback: contentState.feedback,
            details,
            correctAnswer: lastGradeResultRef.current?.correctAnswer,
        };
    }, [
        shouldShowFeedback,
        isCorrect,
        contentState.score,
        contentState.max,
        contentState.feedback,
        playerGradeDetails,
    ]);

    const playerActions = useMemo(
        () => ({
            requestSubmit: () => {
                void submitRef.current?.();
            },
        }),
        []
    );

    const wrapperTheme = useHostTheme ? undefined : config.theme;
    // EAA-1672: expose the same theme decision to the ContentThemeProvider
    // hook seam. ContentWrapper keeps emitting CSS vars via its `theme` prop
    // as before — the provider only distributes the resolved theme to hooks
    // (e.g. useContentTheme), it does not apply vars itself, so nothing is
    // double-applied.
    const themeLayers = useMemo(
        () => ({ config: config.theme, useHostTheme }),
        [config.theme, useHostTheme]
    );
    // EAA-1677: useHostTheme embeds must only inherit an ancestor's data-skin
    // (the host's own <html>/wrapper), never stamp their own — skin is
    // orthogonal to color theming, so it follows the same host-wins rule.
    const effectiveSkin = useHostTheme ? undefined : skin;

    if (!PlayerComponent) {
        return (
            <ContentThemeProvider layers={themeLayers} skin={effectiveSkin}>
                <ContentWrapper
                    className={cn('content-player', className)}
                    theme={wrapperTheme}
                    border={false}
                    skin={effectiveSkin}
                >
                    <div className="content-player-empty text-center py-8 text-muted-foreground">
                        <p>
                            Content type "{content.type}" is not registered or has no player component.
                        </p>
                    </div>
                </ContentWrapper>
            </ContentThemeProvider>
        );
    }

    const details =
        (playerGradeDetails as Record<string, unknown> | undefined) ??
        (lastGradeResultRef.current?.details as Record<string, unknown> | undefined);
    const essayStyleDetails =
        details &&
        typeof details === 'object' &&
        typeof (details as { overall_score_percentage?: unknown }).overall_score_percentage ===
            'number';
    const itemList = details?.items;
    const hasItemList = Array.isArray(itemList);
    const correctCount =
        (details?.correctCount as number | undefined) ??
        (hasItemList
            ? (itemList as { isCorrect?: boolean }[]).filter((i) => i.isCorrect).length
            : undefined);
    const totalCount =
        (details?.totalItems as number | undefined) ??
        (hasItemList ? (itemList as unknown[]).length : undefined) ??
        (countQuestions(content.type, content.data as Record<string, unknown>) || undefined);
    const showScoreSummary =
        shouldShowFeedback &&
        isCorrect !== null &&
        correctCount != null &&
        totalCount != null &&
        contentState.max != null &&
        !essayStyleDetails;

    const showUnifiedFeedback = shouldShowFeedback && isCorrect !== null && parentAllowsFeedback;

    // EAA-1890: Read Aloud grades through SpeechAce and renders its own score card, so it
    // suppresses the generic verdict for the same reason the criteria path does — the
    // flag is `pronunciationGrading`, not `aiGrading`, and checking only the latter left
    // a redundant "Chưa đúng" bar above a panel already showing 82/100.
    const suppressUnifiedFeedbackForSpeakingAi =
        content.type === 'speaking' &&
        Boolean(details && (details.aiGrading === true || details.pronunciationGrading === true));

    // Essay player renders ScoreGradeComponent inline — suppress the generic result bar.
    const suppressUnifiedFeedbackForEssay =
        content.type === 'essay' && essayStyleDetails;

    // EAA-1755: interactive-reading shows its own pronunciation score panel, so the
    // generic correct/incorrect verdict ("Chưa đúng") is redundant — always suppress.
    const suppressUnifiedFeedbackForInteractiveReading = content.type === 'interactive-reading';

    // ELMS-2370: LMS "hide answer" => no verdict bar / "x of y correct" score card at all.
    const showUnifiedFeedbackBlock =
        showUnifiedFeedback &&
        !hideCorrectAnswers &&
        !suppressUnifiedFeedbackForSpeakingAi &&
        !suppressUnifiedFeedbackForEssay &&
        !suppressUnifiedFeedbackForInteractiveReading;

    return (
        <ContentThemeProvider layers={themeLayers} skin={effectiveSkin}>
            <ContentWrapper
                className={cn('content-player', className)}
                theme={wrapperTheme}
                border={false}
                skin={effectiveSkin}
            >
                {showTimerDisplay && timerEnabled && (
                    <div className="mb-4 flex justify-end">
                        <TimerDisplay
                            remaining={remaining}
                            total={config.timer?.duration}
                            isRunning={isRunning}
                            isWarning={isWarning}
                            isExpired={isExpired}
                            showProgress
                        />
                    </div>
                )}

                <ContentPlayerNavLeadingContext.Provider value={registerNavLeading}>
                    <ContentPlayerActionsContext.Provider value={playerActions}>
                        <ContentPlayerSlot
                            PlayerComponent={PlayerComponent}
                            playerData={playerData}
                            learnerAnswer={learnerAnswer}
                            onAnswer={handleAnswerChange}
                            disabled={disabled || !isInteractive}
                            showFeedback={shouldShowFeedback}
                            result={gradingResultForPlayer}
                            isReview={isReview}
                            config={config}
                            tenantId={tenantId}
                            contentId={content.id}
                            sessionToken={sessionToken}
                            language={language}
                        />
                    </ContentPlayerActionsContext.Provider>
                </ContentPlayerNavLeadingContext.Provider>

                {showUnifiedFeedbackBlock ? (
                    <div className="content-player-result my-2 space-y-4">
                        {essayStyleDetails ? (
                            <FeedbackDisplay
                                type={getFeedbackType(isCorrect)}
                                message={
                                    contentState.max != null && contentState.max > 0
                                        ? `${Math.round(
                                              ((contentState.score ?? 0) / contentState.max) * 100
                                          )}%`
                                        : contentState.feedback ||
                                          (isCorrect ? t('correct') : t('incorrect'))
                                }
                            />
                        ) : showScoreSummary ? (
                            <ScoreSummary
                                totalScore={contentState.score ?? 0}
                                max={contentState.max!}
                                correctCount={correctCount!}
                                totalCount={totalCount!}
                            />
                        ) : (
                            <FeedbackDisplay
                                type={getFeedbackType(isCorrect)}
                                message={
                                    contentState.feedback || (isCorrect ? t('correct') : t('incorrect'))
                                }
                            />
                        )}
                    </div>
                ) : null}

                {submitError ? (
                    <p className="text-sm text-destructive content-player-submit-error mt-4">
                        {submitError}
                    </p>
                ) : null}

                {effectiveShowNavigation ? (
                    <div className="content-player-navigation">
                        <NavigationButtons
                            onPrevious={onPrevious}
                            onNext={onNext}
                            onSubmit={() => void handleSubmit()}
                            onRetry={handleRetry}
                            canPrevious={!!onPrevious}
                            canNext={!!onNext}
                            canSubmit={baseSubmitNavEnabled && answerValidForSubmit}
                            canRetry={canRetry}
                            showPrevious={!!onPrevious}
                            showNext={!!onNext && (isCorrect === true || !canAttempt)}
                            showSubmit={baseSubmitNavEnabled && !inlinePlayerSubmit}
                            showRetry={shouldShowFeedback && canRetry && !isReview && !disabled}
                            isSubmitting={isSubmitting}
                            submitDisabledHint={submitDisabledHintNav}
                            leading={navLeading}
                            leadingKey={navLeadingKey}
                            labels={{
                                previous: t('previous'),
                                next: t('next'),
                                submit: t('submit'),
                                retry: t('retry'),
                                submitting: t('submitting'),
                            }}
                        />
                    </div>
                ) : null}
            </ContentWrapper>
        </ContentThemeProvider>
    );
}
